/**
 * Sprint 5C — Dashboard sibling cards (financials / debt / receivables / deals).
 *
 * Single fetch to `/api/dashboard/overview` returning the DASH-002
 * envelope. Each section renders through <DashboardSectionState/> so
 * forbidden/failed/loading states are explicit per DASH-005 + 5B.
 *
 * Frontend does ZERO accounting math — it only formats backend values.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  DollarSign, Landmark, Receipt, Briefcase,
} from 'lucide-react';
import DashboardSectionState from './DashboardSectionState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmtCurrency(v) {
  if (v == null) return '—';
  const num = Number(v);
  if (!isFinite(num)) return '—';
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function useOverview() {
  const { token } = useAuth();
  const [envelope, setEnvelope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topError, setTopError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/api/dashboard/overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setEnvelope(res.data);
          setTopError(null);
        }
      } catch (err) {
        if (!cancelled) {
          const code = err?.response?.status;
          if (code === 401 || code === 403) {
            // Whole route forbidden — synthesize a forbidden envelope.
            setEnvelope({
              sections: {
                financials: { status: 'forbidden' },
                debt: { status: 'forbidden' },
                receivables: { status: 'forbidden' },
                deals: { status: 'forbidden' },
              },
            });
          } else {
            setTopError(err?.message || 'Failed to load Dashboard sections');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);
  return { envelope, loading, topError };
}

function _sectionAt(envelope, key, loading, topError) {
  if (loading) return { status: 'loading', data: null, error: null };
  if (topError) return { status: 'failed', data: null, error: topError };
  return envelope?.sections?.[key]
    || { status: 'failed', data: null, error: `Missing section ${key}` };
}

function _MiniCard({ Icon, title, primary, secondary, accent = 'slate', testId }) {
  const tint = {
    slate:   'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700',
    emerald: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900',
    sky:     'bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-900',
    rose:    'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900',
    amber:   'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900',
  }[accent] || 'bg-slate-50 border-slate-200';
  return (
    <div data-testid={testId} className={`rounded-xl border p-4 ${tint}`}>
      <div className="flex items-center gap-2 text-slate-500 text-xs">
        <Icon className="w-3.5 h-3.5" />
        <span>{title}</span>
      </div>
      <div className="text-2xl font-semibold text-slate-800 dark:text-slate-100 mt-1.5">
        {primary}
      </div>
      {secondary && (
        <div className="text-[11px] text-slate-500 mt-1">{secondary}</div>
      )}
    </div>
  );
}

export default function DashboardSections() {
  const { envelope, loading, topError } = useOverview();
  const generatedAt = envelope?.generated_at;

  const fin = _sectionAt(envelope, 'financials', loading, topError);
  const debt = _sectionAt(envelope, 'debt', loading, topError);
  const recv = _sectionAt(envelope, 'receivables', loading, topError);
  const deals = _sectionAt(envelope, 'deals', loading, topError);

  return (
    <div data-testid="dashboard-sections" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <DashboardSectionState
        status={fin.status} data={fin.data} error={fin.error}
        generatedAt={generatedAt} title="Financials"
        testIdPrefix="dash-financials"
      >
        {(d) => (
          <_MiniCard
            testId="dash-financials-card"
            Icon={DollarSign} title="YTD Cash Flow" accent="emerald"
            primary={fmtCurrency(d?.cash_flow)}
            secondary={`NOI ${fmtCurrency(d?.noi)} · OpEx ${fmtCurrency(d?.total_expense)}`}
          />
        )}
      </DashboardSectionState>

      <DashboardSectionState
        status={debt.status} data={debt.data} error={debt.error}
        generatedAt={generatedAt} title="Debt"
        testIdPrefix="dash-debt"
      >
        {(d) => (
          <_MiniCard
            testId="dash-debt-card"
            Icon={Landmark} title="Total Debt" accent="rose"
            primary={fmtCurrency(d?.total_debt)}
            secondary={`${d?.account_count || 0} liability account(s) · basis ${d?.basis || '—'}`}
          />
        )}
      </DashboardSectionState>

      <DashboardSectionState
        status={recv.status} data={recv.data} error={recv.error}
        generatedAt={generatedAt} title="Receivables"
        testIdPrefix="dash-receivables"
      >
        {(d) => (
          <_MiniCard
            testId="dash-receivables-card"
            Icon={Receipt} title="Past-due Receivables" accent="amber"
            primary={fmtCurrency(d?.total_receivables)}
            secondary={`${d?.delinquent_lease_count || 0} of ${d?.active_lease_count || 0} active lease(s)`}
          />
        )}
      </DashboardSectionState>

      <DashboardSectionState
        status={deals.status} data={deals.data} error={deals.error}
        generatedAt={generatedAt} title="Deals"
        testIdPrefix="dash-deals"
      >
        {(d) => (
          <_MiniCard
            testId="dash-deals-card"
            Icon={Briefcase} title="Active Deals" accent="sky"
            primary={d?.active_deals_count ?? 0}
            secondary={`${d?.underwriting_in_progress_count || 0} UW · ${d?.under_contract_count || 0} UC · ${d?.closing_soon_count || 0} closing`}
          />
        )}
      </DashboardSectionState>
    </div>
  );
}
